import { useUtility } from "../../utils/context/UtilityContext";
import { Header } from "../shared";
import { useNavigate } from "react-router-dom";
import { ManagerLinkCard } from "./ManagerLinkCard";
import {
  FiGrid,
  FiUsers,
  FiImage,
  FiList,
  FiGift,
  FiFileText,
  FiMessageSquare,
  FiYoutube,
  FiBarChart2,
  FiRepeat,
  FiTag,
  FiKey,
  FiPaperclip,
  FiBriefcase,
  FiTrendingUp,
  FiBookOpen,
} from "react-icons/fi";
import { useAuth } from "utils/context";
import { ROLES } from "utils";
import { useMemo } from "react";

// --- New Categorized Link Structure ---
const linkCategories = [
  {
    title: "Store Management",
    links: [
      { label: "Products", path: "/manager/product", icon: FiGrid },
      { label: "Gift Cards", path: "/manager/giftcard", icon: FiKey },
      { label: "Promo Codes", path: "/manager/PromoCodeManager", icon: FiTag },
    ],
  },
  {
    title: "User Management",
    links: [{ label: "Users", path: "/manager/users", icon: FiUsers }],
  },
  {
    title: "Analytics & Accounting",
    links: [
      { label: "Statistics", path: "/manager/stats", icon: FiBarChart2 },
      { label: "Reports", path: "/manager/reports", icon: FiBarChart2 },
      {
        label: "Manual Journal Entry",
        path: "/manager/accounting/manual-entry",
        icon: FiBookOpen,
      },
      {
        label: "Add Transaction",
        path: "/manager/transaction",
        icon: FiRepeat,
      },
      { label: "Operations", path: "/manager/operations", icon: FiTrendingUp },
    ],
  },
  {
    title: "Tools",
    links: [{ label: "SMS", path: "/manager/sms", icon: FiMessageSquare }],
  },
  {
    title: "Content Management",
    links: [
      { label: "Providers", path: "/manager/providers", icon: FiBriefcase },
      { label: "Steps List", path: "/manager/steps", icon: FiList },
      { label: "Mottos List", path: "/manager/mottos", icon: FiGift },
      { label: "Predefined Lists", path: "/manager/list", icon: FiPaperclip },
      {
        label: "Rich Text Pages",
        path: "/manager/rich-text",
        icon: FiFileText,
      },
      { label: "YouTube URLs", path: "/manager/youtubeUrl", icon: FiYoutube },
      { label: "Images", path: "/manager/images", icon: FiImage },
    ],
  },
];

// This list remains the same, defining which links are visible to limited roles
const LIMITED_PATHS = new Set([
  "/manager/operations",
  "/manager/sms",
  "/manager/stats",
  "/manager/users",
]);

export const Manager = () => {
  const navigate = useNavigate();
  const { marginBottomHeader } = useUtility();
  const { role } = useAuth();

  // Updated filtering logic to work with the new categorized structure
  const visibleCategories = useMemo(() => {
    if (role === ROLES.ADMIN) {
      return linkCategories; // Admins see all categories and links
    }

    if (role === ROLES.MANAGER || role === ROLES.PARTNER) {
      // Filter links within each category
      return (
        linkCategories
          .map((category) => ({
            ...category,
            links: category.links.filter((link) =>
              LIMITED_PATHS.has(link.path)
            ),
          }))
          // Then, filter out any categories that are now empty
          .filter((category) => category.links.length > 0)
      );
    }

    // Non-privileged roles see nothing
    return [];
  }, [role]);

  return (
    <div className={`${marginBottomHeader} p-4 md:p-8`}>
      <Header />
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        Manager Dashboard
      </h1>

      {/* Render links by category */}
      <div className="flex flex-col gap-10">
        {visibleCategories.map((category) => (
          <section key={category.title}>
            <h2 className="text-2xl font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
              {category.title}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
              {category.links.map((link) => (
                <ManagerLinkCard
                  key={link.path}
                  label={link.label}
                  icon={link.icon}
                  onClick={() => navigate(link.path)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};
